import React, { useMemo } from 'react';
import PageHeading from '../../../../components/PageHeading';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

export default function ProjectOver({ projectData }) {

    const formattedValue = projectData?.plottedLandArea
        ? (
            Number(projectData.plottedLandArea) % 1 === 0
                ? projectData.plottedLandArea.toString()
                : Number(projectData.plottedLandArea).toFixed(2)
        )
        : '0';

    const unit = projectData?.unitName === "SquareFoot"
        ? "Sq"
        : projectData?.unitName || '';

    const options = useMemo(() => ({
        chart: {
            type: 'pie',
            height: null,
            width: null,
            spacing: [10, 10, 10, 10],

            events: {
                render: function () {
                    const chart = this;
                    const series = chart.series[0];

                    if (!series || !series.center) return;

                    const centerX = series.center[0] + chart.plotLeft;
                    const centerY = series.center[1] + chart.plotTop;

                    /*
                     * Use the actual pie diameter so the font
                     * responds to the chart size.
                     */
                    const diameter = series.center[2];

                    const titleFontSize = Math.max(
                        12,
                        Math.min(18, diameter * 0.09)
                    );

                    const valueFontSize = Math.max(
                        14,
                        Math.min(24, diameter * 0.11)
                    );

                    // Remove old labels
                    if (chart.customLabel1) {
                        chart.customLabel1.destroy();
                    }

                    if (chart.customLabel2) {
                        chart.customLabel2.destroy();
                    }

                    // -------------------------
                    // Plotted Area
                    // -------------------------

                    chart.customLabel1 = chart.renderer
                        .text('Plotted Area', 0, 0)
                        .css({
                            color: '#000',
                            fontSize: `${titleFontSize}px`,
                            fontWeight: '600'
                        })
                        .add();

                    const bbox1 = chart.customLabel1.getBBox();

                    chart.customLabel1.attr({
                        x: centerX - bbox1.width / 2,
                        y: centerY - 5,
                        zIndex: 99
                    });

                    // -------------------------
                    // Area Value
                    // -------------------------

                    chart.customLabel2 = chart.renderer
                        .text(formattedValue, 0, 0)
                        .css({
                            color: '#000',
                            fontSize: `${valueFontSize}px`,
                            fontWeight: '800'
                        })
                        .add();

                    const bbox2 = chart.customLabel2.getBBox();

                    chart.customLabel2.attr({
                        x: centerX - bbox2.width / 2,
                        y: centerY + valueFontSize,
                        zIndex: 99
                    });
                }
            }
        },

        title: {
            text: ''
        },

        credits: {
            enabled: false
        },

        exporting: {
            enabled: false
        },

        tooltip: {
            enabled: false
        },

        plotOptions: {
            pie: {
                innerSize: '70%',
                dataLabels: {
                    enabled: false
                },
                borderWidth: 0
            }
        },

        series: [
            {
                name: 'Amount',
                data: [
                    {
                        name: 'Total Land Cost',
                        y: projectData?.totalLandPrice
                            ? parseFloat(
                                projectData.totalLandPrice.split(" ")[0]
                            )
                            : 0,
                        color: '#006CFF'
                    },
                    {
                        name: 'Total amount from booking',
                        y: 0,
                        color: '#FE996C'
                    },
                    {
                        name: 'Sale Amount received',
                        y: 0,
                        color: '#03D4C6'
                    },
                    {
                        name: 'Amount left to be received',
                        y: 0,
                        color: '#F15642'
                    }
                ]
            }
        ]
    }), [projectData, formattedValue]);


    const areaRows = [
        {
            color: '#006CFF',
            title: 'Plotted Area',
            value: projectData?.plottedLandArea
                ? `${Number(projectData.plottedLandArea).toFixed(2)} ${unit}`
                : '-',
            amount: `₹ ${projectData?.totalLandPrice || 0}`
        },
        {
            color: '#FFFFFF',
            border: true,
            title: 'Available Area',
            value: projectData?.availableArea
                ? `${Number(projectData.availableArea).toFixed(2)} ${unit}`
                : '-',
            amount: `₹ ${projectData?.availableAreaPrice || 0}`
        },
        {
            color: '#8DC86C',
            title: 'Sold Area',
            value: projectData?.saleArea
                ? `${Number(projectData.saleArea).toFixed(2)} ${unit}`
                : '-',
            amount: `₹ ${projectData?.saleAreaPrice || 0}`
        },
        {
            color: '#FEFA99',
            title: 'Booked Area',
            value: projectData?.bookedArea
                ? `${Number(projectData.bookedArea).toFixed(2)} ${unit}`
                : '-',
            amount: `₹ ${projectData?.bookedAreaPrice || 0}`
        }
    ];

    console.log("projectData", projectData)
    return (
        <div className="container-fluid landformconatiner p-3 p-md-4">

            <PageHeading pagename="Project Overview" />

            {/* Chart */}
            <div className="row">
                <div className="col-12">

                    <div
                        style={{
                            width: '100%',
                            height: 'clamp(250px, 32vw, 350px)',
                            minHeight: '250px'
                        }}
                    >
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            containerProps={{
                                style: {
                                    width: '100%',
                                    height: '100%'
                                }
                            }}
                        />
                    </div>

                </div>
            </div>


            {/* Area List */}
            <div className="row mt-2">
                <div className="col-12">

                    <div className="d-flex flex-column gap-3 pt-2">

                        {areaRows.map((item, index) => (
                            <div
                                key={index}
                                className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center pb-2 rowBox"
                                style={{
                                    width: '100%',
                                    gap: '10px'
                                }}
                            >

                                {/* Left */}
                                <div
                                    className="d-flex flex-row gap-2"
                                    style={{
                                        minWidth: 0,
                                        flex: 1
                                    }}
                                >

                                    <div
                                        style={{
                                            width: '15px',
                                            height: '15px',
                                            minWidth: '15px',
                                            backgroundColor: item.color,
                                            borderRadius: '50%',
                                            border: item.border
                                                ? '1px solid black'
                                                : 'none'
                                        }}
                                    />

                                    <span
                                        className="d-flex flex-column"
                                        style={{
                                            minWidth: 0
                                        }}
                                    >
                                        <label className="heading-project fw-semibold">
                                            {item.title}
                                        </label>

                                        <label className="sub-heading-project text-break">
                                            {item.value}
                                        </label>
                                    </span>

                                </div>


                                {/* Right */}
                                <div
                                    className="d-flex flex-row"
                                    style={{
                                        flexShrink: 0
                                    }}
                                >
                                    <PageHeading pagename={item.amount} />
                                </div>

                            </div>
                        ))}

                    </div>

                </div>
            </div>

        </div>
    );
}